import dayjs from "dayjs";
import AnnotationCC from "../../api/AnnotationCommunicationController";
import Task from "./Task";

export const isTaskDataValid = (data: TaskData) => {
    return (
        !isNaN(data.dataset) &&
        data.dataset >= 0 &&
        !isNaN(data.annotation_interface) &&
        data.annotation_interface >= 0 &&
        data.physicians.filter(p => !p.deadline || dayjs(p.deadline).isBefore(dayjs())).length === 0
    );
};

export type AssignmentType = {
    user: number;
    deadline: string | undefined;
};

// Annotazione di partenza di un media del dataset: il contenuto è libero, il server
// lo salva così com'è e sono i tool di annotazione a interpretarlo.
export type InitialAnnotation = {
    media: number;
    result: any;
};

export class TaskData {
    dataset: number = -1;
    annotation_interface: number = -1;
    physicians: AssignmentType[] = [];
    // Opzionale: si possono impostare solo alla creazione del task.
    initial_annotations?: InitialAnnotation[];
}

export type TaskDataKey = keyof TaskData;

export type TaskConflictsMatrix = {
    [key: number]: {
        [key: number]: {
            common_annotations: number;
            agreement_degree: number;
        };
    };
    fn: string;
};

export type PhysicianStatus = {
    annotations: number;
    deadline: string;
    task_url: string;
    physician: number;
};

export default class TaskStatus extends Task {
    physicians: PhysicianStatus[];

    print_function: string;
    conflict_functions: string[];

    constructor(obj: TaskStatus) {
        super(obj);
        this.physicians = obj.physicians;
        this.print_function = obj.print_function;
        this.conflict_functions = obj.conflict_functions;
    }

    getData = (): TaskData => {
        return {
            dataset: this.dataset,
            annotation_interface: this.annotation_interface,
            physicians: this.physicians.map(p => ({
                user: p.physician,
                deadline: p.deadline
            }))
        };
    };

    getAnnotations = (): Promise<any> => {
        return AnnotationCC.getAnnotations(this.id);
    };

    // Il server accetta un solo assegnamento per chiamata (v. AnnotationCC.updateTaskAssignment),
    // e questo metodo rispecchia lo stesso vincolo: un solo AssignmentType, non un
    // array. Per aggiornarne più di uno bisogna chiamarlo più volte dal chiamante
    // (v. TaskDetailsForm.tsx, che usa filterAssignments per sapere quali farlo).
    updateAssignment = async (assignment: AssignmentType): Promise<boolean> => {
        this.physicians = await AnnotationCC.updateTaskAssignment(this.id, assignment);
        return true;
    };

    removeAssignment = async (userId: number): Promise<void> => {
        await AnnotationCC.removeTaskAssignment(this.id, userId);
        this.physicians = this.physicians.filter(p => p.physician !== userId);
    };

    filterAssignments = (assignments: AssignmentType[]): AssignmentType[] => {
        return assignments.filter(a => {
            const physician = this.physicians.find(p => p.physician === a.user);
            if (!physician) return true;
            return physician.deadline !== a.deadline;
        });
    };

    conflictMatrix = (conflictFunction: string): Promise<TaskConflictsMatrix> => {
        return AnnotationCC.getConflictMatrix(this.id, conflictFunction);
    };

    getConflicts = (user: number, other: number, conflictFunction: string, cnt: number, offset: number) => {
        return AnnotationCC.getTaskConflicts(this.id, user, other, conflictFunction, cnt, offset);
    };
}
